import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Mail, Upload, FileText, ArrowRight, Sparkles, AlertCircle } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/contexts/AuthContext'

export function InvestorSubmissionPage() {
  const { user } = useAuth()
  const [limitReached, setLimitReached] = useState(false)
  const [checking, setChecking] = useState(true)

  useEffect(() => {
    const checkUsage = async () => {
      if (!user) return
      try {
        const token = await user.getIdToken()
        const res = await fetch(`${import.meta.env.VITE_API_BASE_URL || ''}/api/subscription/current`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
        if (res.ok) {
          const data = await res.json()
          if (data.usage && !data.usage.allowed) {
            setLimitReached(true)
          }
        }
      } catch (e) {
        console.error(e)
      } finally {
        setChecking(false)
      }
    }
    checkUsage()
  }, [user])

  if (limitReached) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 p-6 md:p-12 flex items-center justify-center">
        <Card className="max-w-2xl w-full border-red-200 dark:border-red-900 shadow-xl">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mb-4">
              <AlertCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
            </div>
            <CardTitle className="text-2xl font-bold text-red-600 dark:text-red-400">Analysis Limit Reached</CardTitle>
            <CardDescription className="text-lg mt-2">
              You have used all your available analyses for the current billing period.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-slate-600 dark:text-slate-300">
              Upgrade your plan to continue analyzing more startups immediately.
            </p>
          </CardContent>
          <CardFooter className="flex justify-center gap-4">
            <Link to="/plans">
              <Button size="lg" className="bg-logo-gradient text-white">
                View Plans & Upgrade
              </Button>
            </Link>
            <Link to="/dashboard">
              <Button variant="outline" size="lg">
                Back to Dashboard
              </Button>
            </Link>
          </CardFooter>
        </Card>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 p-6 md:p-12">
      <div className="max-w-7xl mx-auto space-y-12">
        {/* Header */}
        <div className="text-center space-y-4">
          <h1 className="text-4xl md:text-5xl font-display font-bold bg-gradient-to-r from-slate-900 to-slate-700 dark:from-white dark:to-slate-300 bg-clip-text text-transparent">
            New Evaluation
          </h1>
          <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
            Choose how you want to submit a startup for analysis. We offer flexible options to suit your workflow.
          </p>
        </div>

        {/* Options Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">

          {/* Option 1: Forward Email */}
          <Card className="relative overflow-hidden group hover:shadow-xl transition-all duration-300 border-2 border-slate-200 dark:border-slate-800 hover:border-blue-500/50 dark:hover:border-blue-500/50 bg-white dark:bg-slate-900">
            <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
              <Mail className="w-32 h-32 text-blue-500" />
            </div>
            <CardHeader className="relative z-10">
              <div className="w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex items-center justify-center mb-4">
                <Mail className="w-6 h-6" />
              </div>
              <CardTitle className="text-2xl font-bold text-slate-900 dark:text-white">Forward to Analyze</CardTitle>
            </CardHeader>
            <CardContent className="relative z-10 space-y-6">
              <CardDescription className="text-base text-slate-600 dark:text-slate-300 leading-relaxed">
                Just forward the pitch email with documents to our analysis bot. We'll extract the data and create the analysis right away.
              </CardDescription>

              <div className="p-4 bg-slate-100 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                <p className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-1">Forward to:</p>
                <code className="text-lg font-bold text-blue-600 dark:text-blue-400 select-all">
                  analyze@uniscout.ai
                </code>
              </div>

              <div className="flex items-center text-sm text-slate-500">
                <Sparkles className="w-4 h-4 mr-2 text-yellow-500" />
                <span>Instant processing</span>
              </div>
            </CardContent>
          </Card>

          {/* Option 2: Drop Docs */}
          <Link to="/investor-submission/drop-docs" className="block h-full">
            <Card className="relative h-full overflow-hidden group hover:shadow-xl transition-all duration-300 border-2 border-slate-200 dark:border-slate-800 hover:border-purple-500/50 dark:hover:border-purple-500/50 cursor-pointer bg-white dark:bg-slate-900">
              <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                <Upload className="w-32 h-32 text-purple-500" />
              </div>
              <CardHeader className="relative z-10">
                <div className="w-12 h-12 rounded-xl bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 flex items-center justify-center mb-4">
                  <Upload className="w-6 h-6" />
                </div>
                <CardTitle className="text-2xl font-bold text-slate-900 dark:text-white">Drop the Docs</CardTitle>
              </CardHeader>
              <CardContent className="relative z-10 space-y-6">
                <CardDescription className="text-base text-slate-600 dark:text-slate-300 leading-relaxed">
                  Upload the pitch deck and other documents. Provide 4-5 basic details, and we'll handle the rest of the analysis.
                </CardDescription>

                <div className="mt-auto pt-4">
                  <Button className="w-full bg-logo-gradient text-white border-none shadow-lg hover:opacity-90">
                    Start Upload
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </Link>

          {/* Option 3: Detailed Form */}
          <Link to="/investor-submission/form" className="block h-full">
            <Card className="relative h-full overflow-hidden group hover:shadow-xl transition-all duration-300 border-2 border-slate-200 dark:border-slate-800 hover:border-emerald-500/50 dark:hover:border-emerald-500/50 cursor-pointer bg-white dark:bg-slate-900">
              <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                <FileText className="w-32 h-32 text-emerald-500" />
              </div>
              <CardHeader className="relative z-10">
                <div className="w-12 h-12 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 flex items-center justify-center mb-4">
                  <FileText className="w-6 h-6" />
                </div>
                <CardTitle className="text-2xl font-bold text-slate-900 dark:text-white">Detailed Form</CardTitle>
              </CardHeader>
              <CardContent className="relative z-10 space-y-6">
                <CardDescription className="text-base text-slate-600 dark:text-slate-300 leading-relaxed">
                  Fill out a comprehensive structured form with detailed fields for deep customization and precision.
                </CardDescription>

                <div className="mt-auto pt-4">
                  <Button variant="outline" className="w-full border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-800">
                    Open Form
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </Link>

        </div>
      </div>
    </div>
  )
}
